import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Swords, ArrowLeft, Crown, Medal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PvpLeaderboard() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pvp/leaderboard`, {
        credentials: 'include'
      });
      const data = await response.json();
      setLeaderboard(data);
    } catch (error) {
      toast.error("Помилка завантаження таблиці лідерів");
    } finally {
      setLoading(false);
    }
  };

  const getRankColor = (rankTier) => {
    const colors = {
      "Master": "from-purple-500 to-pink-500",
      "Platinum": "from-cyan-500 to-blue-500",
      "Gold": "from-yellow-500 to-orange-500",
      "Silver": "from-gray-400 to-gray-500",
      "Bronze": "from-orange-800 to-orange-900"
    };
    return colors[rankTier] || "from-gray-600 to-gray-700";
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-orange-600" />;
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="grid-background min-h-screen">
        <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button
                variant="ghost"
                onClick={() => navigate('/pvp')}
                data-testid="back-button"
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Назад</span>
              </Button>
              <h1 className="text-xl font-bold text-foreground">PvP Таблиця Лідерів</h1>
              <div className="w-24"></div>
            </div>
          </div>
        </nav>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-red-500 mb-4">
                <Swords className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-4xl font-black text-foreground mb-2">Battle Champions</h2>
              <p className="text-lg text-muted-foreground">Найкращі архітектори в PvP режимі</p>
            </div>

            {leaderboard.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-6">Таблиця лідерів порожня. Будьте першим!</p>
                <Button
                  onClick={() => navigate('/pvp')}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                >
                  Почати Battle
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {leaderboard.map((entry, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`bg-card/50 backdrop-blur-sm border p-6 rounded-xl flex items-center space-x-4 ${
                      entry.rank <= 3
                        ? 'border-orange-500/50 shadow-[0_0_20px_rgba(249,115,22,0.2)]'
                        : 'border-white/5'
                    } hover:border-orange-500/30 transition-all`}
                  >
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-muted font-bold text-foreground">
                      {getRankIcon(entry.rank) || entry.rank}
                    </div>
                    
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={entry.picture} alt={entry.name} />
                      <AvatarFallback>{entry.name?.charAt(0)}</AvatarFallback>
                    </Avatar>

                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-foreground">{entry.name}</h3>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <span>{entry.wins}W - {entry.losses}L</span>
                        {entry.wins + entry.losses > 0 && (
                          <span>
                            {Math.round((entry.wins / (entry.wins + entry.losses)) * 100)}% WR
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-right space-y-2">
                      <div className={`inline-block px-4 py-1 rounded-full bg-gradient-to-r ${getRankColor(entry.rank_tier)}`}>
                        <span className="text-white font-bold text-sm">{entry.rank_tier}</span>
                      </div>
                      <div className="text-2xl font-bold text-foreground">{entry.elo_rating}</div>
                      <div className="text-xs text-muted-foreground">ELO Rating</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            <div className="text-center mt-8">
              <Button
                onClick={() => navigate('/pvp')}
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white h-14 px-10 text-lg rounded-xl font-bold"
              >
                <Swords className="w-5 h-5 mr-2" />
                Почати Battle
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
